import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// SPRINT-5: Initialize i18n before app renders
import './i18n/i18n';
import i18n from './i18n/i18n';
import { I18nextProvider } from 'react-i18next';
import PublicLegalShell, { matchPublicLegalPath } from '@/PublicLegalShell';
import PublicDataDeletionShell, { matchDataDeletionPath } from '@/PublicDataDeletionShell';
import { normalizeLegacyPublicHashUrl } from '@/routing/publicSurface';

// TREATX-APPLE-MOBILE-CALLBACK-BLANK-SCREEN-01
// Apple's Sign in with Apple redirect flow returns the user to
// `${origin}/auth/apple/callback#id_token=…&code=…&state=…` (a real
// pathname). This app uses HashRouter, which only matches routes based
// on the URL hash — since Apple's payload IS the hash, HashRouter
// cannot see the `/auth/apple/callback` route and falls back to the
// homepage. We rewrite the URL to `${origin}/#/auth/apple/callback?…`
// BEFORE React mounts so HashRouter dispatches to <AppleCallback />
// and the params live in the hash-router search string.
(function normalizeAppleCallbackUrl() {
  try {
    if (typeof window === 'undefined') return;
    if (window.location.pathname !== '/auth/apple/callback') return;
    const rawHash = (window.location.hash || '').replace(/^#/, '');
    const rawSearch = (window.location.search || '').replace(/^\?/, '');
    const params = rawHash || rawSearch;
    const newUrl = window.location.origin + '/#/auth/apple/callback'
      + (params ? ('?' + params) : '');
    window.history.replaceState({}, '', newUrl);
  } catch (e) { /* fail-open: React will render the homepage */ }
})();

// TREATX-SEO-RECOVERY-S2 — legacy public hash URLs (`/#/about`) are rewritten
// to their clean equivalent (`/about`) before React mounts, so a shared or
// bookmarked link lands on the canonical URL and the public router owns the
// load. Private hash URLs — invitations, password resets, payment returns and
// every link in an already-delivered email — are left untouched. Runs AFTER
// the Apple normaliser above so a token payload in the hash is never mistaken
// for a route.
normalizeLegacyPublicHashUrl();

const root = ReactDOM.createRoot(document.getElementById("root"));

// LEGAL-PUBLIC-CANONICAL-URL-02 — /legal/<key> is served by a standalone
// shell (MemoryRouter) so the canonical hash-free URL stays in the address
// bar for users, Meta and crawlers alike. Everything else boots the normal
// HashRouter app, including the legacy #/legal/<key> URLs.
const publicLegalKey = matchPublicLegalPath(
  typeof window !== 'undefined' ? window.location.pathname : ''
);

// TREATX-DATA-DELETION-INSTRUCTIONS-01 — /data-deletion is served by the same
// standalone-shell approach so the hash-free URL Meta is given stays intact.
const isDataDeletionPath = matchDataDeletionPath(
  typeof window !== 'undefined' ? window.location.pathname : ''
);

root.render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      {publicLegalKey
        ? <PublicLegalShell contentKey={publicLegalKey} />
        : isDataDeletionPath
          ? <PublicDataDeletionShell />
          : <App />}
    </I18nextProvider>
  </React.StrictMode>,
);
